import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { Workflow } from '@compass/client/types/workflow';

import type { ActionDataParams } from '@/types/actions';
import type { FieldGroup } from '@/types/formSchema';
import {
  actionRequiresInput,
  getAvailableActionsForCaseState,
  transformActionForHumanCall,
} from '@/utils/caseActions';
import { validateGroup } from '@/utils/cases';
import { parseUIGroups } from '@/utils/formSchema';
import { applyRequiredFieldsToGroups } from '@/utils/schemaValidation';

export interface StatusGroup {
  status: string;
  cases: GetCaseRecordResponse[];
  count: number;
  availableActions: Array<{ value: string; label: string }>;
  selectedAction?: string;
}

export interface StatusUpdatePlan {
  action: string;
  caseIds: string[];
  count: number;
  actionDataParams?: ActionDataParams;
  caseState?: string;
}

// Group cases by their current status
export const groupCasesByStatus = (
  cases: GetCaseRecordResponse[],
  workflow?: Workflow | null,
  // Whether the user may act on cases in a state. A state they may not (a Program-Admin-only
  // human audit, BV-2657) keeps its group but offers no actions.
  canActOnState: (state: string) => boolean = () => true,
): StatusGroup[] => {
  if (!cases.length || !workflow) {
    return [];
  }

  // Group cases by status
  const statusGroups = new Map<string, GetCaseRecordResponse[]>();

  cases.forEach(case_ => {
    if (!case_.state) return;

    if (!statusGroups.has(case_.state)) {
      statusGroups.set(case_.state, []);
    }
    statusGroups.get(case_.state)!.push(case_);
  });

  // Convert to StatusGroup array with available actions
  return Array.from(statusGroups.entries()).map(([status, casesInStatus]) => ({
    status,
    cases: casesInStatus,
    count: casesInStatus.length,
    availableActions: canActOnState(status)
      ? getAvailableActionsForCaseState(workflow, status)
      : [],
    selectedAction: undefined,
  }));
};

// Build status update plans from status groups
export const buildStatusUpdatePlans = (statusGroups: StatusGroup[]): StatusUpdatePlan[] => {
  return statusGroups
    .filter(group => group.selectedAction) // Only groups with selected actions
    .map(group => ({
      action: transformActionForHumanCall(group.selectedAction!, group.status),
      caseIds: group.cases.map(case_ => case_.id!),
      count: group.count,
      caseState: group.status,
    }));
};

// Validate that at least one status group has a valid selection
export const validateStatusGroups = (statusGroups: StatusGroup[]): boolean => {
  // Check if at least one group has a valid selected action
  return statusGroups.some(
    group =>
      group.selectedAction &&
      group.availableActions.length > 0 &&
      group.availableActions.some(
        action => transformActionForHumanCall(action.value, group.status) === group.selectedAction,
      ),
  );
};

// Get summary text for confirmation dialog
export const getStatusUpdateSummary = (statusGroups: StatusGroup[]): string => {
  const updates = statusGroups
    .filter(group => group.selectedAction)
    .map(
      group =>
        `${group.count} case${group.count === 1 ? '' : 's'} from "${group.status}" → "${group.selectedAction}"`,
    );

  return `You are about to update ${updates.length} group${updates.length === 1 ? '' : 's'} of cases:\n\n${updates.join('\n')}`;
};

// Get total count of cases that will be updated
export const getTotalUpdateCount = (statusGroups: StatusGroup[]): number => {
  return statusGroups
    .filter(group => group.selectedAction)
    .reduce((total, group) => total + group.count, 0);
};

/**
 * Filter out actions that require user input.
 * Used for bulk operations where per-action input collection is not supported.
 */
export const filterActionsForBulkUpdate = (
  actions: Array<{ value: string; label: string }>,
): Array<{ value: string; label: string }> => {
  return actions.filter(action => !actionRequiresInput(action.value));
};

// Actions that require form field validation before execution
export const ACTIONS_REQUIRING_VALIDATION = ['submit_for_review', 'submit_to_manufacturer'];

/**
 * Check if an action requires form field validation before execution.
 * Used for bulk operations to validate cases before state transitions.
 */
export const actionRequiresValidation = (action: string): boolean => {
  return ACTIONS_REQUIRING_VALIDATION.includes(action);
};

// Validation result for a single case
export interface CaseValidationResult {
  caseId: string;
  caseCode?: string;
  isValid: boolean;
  errors: string[];
}

/**
 * Validate a single case's input_data against workflow schema.
 * 1. Parse UI groups from workflow schema
 * 2. Apply JSON schema-based conditional required fields (e.g., if case_type is PQC, lot_number is required)
 * 3. Validate each group against the form data
 */
export function validateCaseForAction(
  case_: GetCaseRecordResponse,
  workflow: Workflow | null,
): CaseValidationResult {
  const caseId = case_.id || '';
  const caseCode = case_.code;

  if (!workflow?.ui) {
    return { caseId, caseCode, isValid: true, errors: [] };
  }

  // Step 1: Parse UI groups from workflow schema
  const inputGroups: FieldGroup[] = parseUIGroups(workflow.ui, 'input');

  return validateCaseWithInputGroups(case_, inputGroups);
}

/**
 * Validate a single case's input_data against input groups from schema service.
 * This is the new API that accepts inputGroups directly instead of workflow.
 * @param case_ - The case record to validate
 * @param inputGroups - Input field groups from schema service
 */
function validateCaseWithInputGroups(
  case_: GetCaseRecordResponse,
  inputGroups: FieldGroup[],
): CaseValidationResult {
  const caseId = case_.id || '';
  const caseCode = case_.code;

  if (!inputGroups.length) {
    return { caseId, caseCode, isValid: true, errors: [] };
  }

  const formData = (case_.input_data?.data as Record<string, unknown>) || {};

  // Apply JSON schema-based conditional required fields
  // This is the same logic used in KnipperCaseSummaryView.validateForm()
  const fieldGroupsWithRequired = applyRequiredFieldsToGroups(inputGroups, formData);

  // Validate each group against the form data
  const allErrors: string[] = [];
  fieldGroupsWithRequired.forEach(group => {
    const groupErrors = validateGroup(group, formData);
    allErrors.push(...Object.values(groupErrors));
  });

  return {
    caseId,
    caseCode,
    isValid: allErrors.length === 0,
    errors: allErrors,
  };
}

/**
 * Validate multiple cases and separate them into valid and invalid sets.
 * For actions that don't require validation, all cases are considered valid.
 */
export function validateCasesForAction(
  cases: GetCaseRecordResponse[],
  action: string,
  workflow: Workflow | null,
): { validCases: GetCaseRecordResponse[]; invalidCases: CaseValidationResult[] } {
  // If action doesn't require validation, all cases are valid
  if (!actionRequiresValidation(action)) {
    return { validCases: cases, invalidCases: [] };
  }

  const validCases: GetCaseRecordResponse[] = [];
  const invalidCases: CaseValidationResult[] = [];

  cases.forEach(case_ => {
    const result = validateCaseForAction(case_, workflow);
    if (result.isValid) {
      validCases.push(case_);
    } else {
      invalidCases.push(result);
    }
  });

  return { validCases, invalidCases };
}

/**
 * Validate multiple cases using input groups from schema service.
 * This is the new API that accepts inputGroups directly instead of workflow.
 * @param cases - Array of case records to validate
 * @param action - The action being performed
 * @param inputGroups - Input field groups from schema service
 */
export function validateCasesWithInputGroups(
  cases: GetCaseRecordResponse[],
  action: string,
  inputGroups: FieldGroup[],
): { validCases: GetCaseRecordResponse[]; invalidCases: CaseValidationResult[] } {
  // If action doesn't require validation, all cases are valid
  if (!actionRequiresValidation(action)) {
    return { validCases: cases, invalidCases: [] };
  }

  const validCases: GetCaseRecordResponse[] = [];
  const invalidCases: CaseValidationResult[] = [];

  cases.forEach(case_ => {
    const result = validateCaseWithInputGroups(case_, inputGroups);
    if (result.isValid) {
      validCases.push(case_);
    } else {
      invalidCases.push(result);
    }
  });

  return { validCases, invalidCases };
}
